import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface Theme {
  id: string;
  name: string;
  colors: {
    primary: {
      50: string;
      100: string;
      200: string;
      300: string;
      400: string;
      500: string;
      600: string;
      700: string;
      800: string;
      900: string;
    };
    background: {
      primary: string;
      secondary: string;
      card: string;
      hover: string;
    };
    text: {
      primary: string;
      secondary: string;
      accent: string;
    };
    border: {
      primary: string;
      secondary: string;
    };
    gradient: {
      primary: string;
      secondary: string;
    };
  };
  styles: {
    sidebar: {
      background: string;
      border: string;
    };
    header: {
      background: string;
      border: string;
    };
    card: {
      background: string;
      border: string;
      shadow: string;
    };
  };
}

const themes: Record<string, Theme> = {
  ocean: {
    id: 'ocean',
    name: 'Ocean Blue',
    colors: {
      primary: {
        50: '#eff6ff',
        100: '#dbeafe',
        200: '#bfdbfe',
        300: '#93c5fd',
        400: '#60a5fa',
        500: '#3b82f6',
        600: '#2563eb',
        700: '#1d4ed8',
        800: '#1e40af',
        900: '#1e3a8a',
      },
      background: {
        primary: '#f8fafc',
        secondary: '#ffffff',
        card: '#ffffff',
        hover: '#f1f5f9',
      },
      text: {
        primary: '#1e293b',
        secondary: '#64748b',
        accent: '#2563eb',
      },
      border: {
        primary: '#e2e8f0',
        secondary: '#cbd5e1',
      },
      gradient: {
        primary: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        secondary: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      },
    },
    styles: {
      sidebar: {
        background: 'bg-white',
        border: 'border-gray-200',
      },
      header: {
        background: 'bg-white',
        border: 'border-gray-200',
      },
      card: {
        background: 'bg-white',
        border: 'border-gray-200',
        shadow: 'shadow-sm hover:shadow-md',
      },
    },
  },
  forest: {
    id: 'forest',
    name: 'Forest Green',
    colors: {
      primary: {
        50: '#f0fdf4',
        100: '#dcfce7',
        200: '#bbf7d0',
        300: '#86efac',
        400: '#4ade80',
        500: '#22c55e',
        600: '#16a34a',
        700: '#15803d',
        800: '#166534',
        900: '#14532d',
      },
      background: {
        primary: '#f9fafb',
        secondary: '#ffffff',
        card: '#ffffff',
        hover: '#f3f4f6',
      },
      text: {
        primary: '#111827',
        secondary: '#6b7280',
        accent: '#16a34a',
      },
      border: {
        primary: '#e5e7eb',
        secondary: '#d1d5db',
      },
      gradient: {
        primary: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        secondary: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
      },
    },
    styles: {
      sidebar: {
        background: 'bg-white',
        border: 'border-gray-200',
      },
      header: {
        background: 'bg-white',
        border: 'border-gray-200',
      },
      card: {
        background: 'bg-white',
        border: 'border-gray-200',
        shadow: 'shadow-sm hover:shadow-md',
      },
    },
  },
  sunset: {
    id: 'sunset',
    name: 'Sunset Orange',
    colors: {
      primary: {
        50: '#fff7ed',
        100: '#ffedd5',
        200: '#fed7aa',
        300: '#fdba74',
        400: '#fb923c',
        500: '#f97316',
        600: '#ea580c',
        700: '#c2410c',
        800: '#9a3412',
        900: '#7c2d12',
      },
      background: {
        primary: '#fefcfb',
        secondary: '#ffffff',
        card: '#ffffff',
        hover: '#fef7f0',
      },
      text: {
        primary: '#1c1917',
        secondary: '#78716c',
        accent: '#ea580c',
      },
      border: {
        primary: '#f5f5f4',
        secondary: '#e7e5e4',
      },
      gradient: {
        primary: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
        secondary: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
      },
    },
    styles: {
      sidebar: {
        background: 'bg-white',
        border: 'border-stone-200',
      },
      header: {
        background: 'bg-white',
        border: 'border-stone-200',
      },
      card: {
        background: 'bg-white',
        border: 'border-stone-200',
        shadow: 'shadow-sm hover:shadow-md',
      },
    },
  },
  midnight: {
    id: 'midnight',
    name: 'Midnight Dark',
    colors: {
      primary: {
        50: '#f8fafc',
        100: '#f1f5f9',
        200: '#e2e8f0',
        300: '#cbd5e1',
        400: '#94a3b8',
        500: '#64748b',
        600: '#475569',
        700: '#334155',
        800: '#1e293b',
        900: '#0f172a',
      },
      background: {
        primary: '#0f172a',
        secondary: '#1e293b',
        card: '#1e293b',
        hover: '#334155',
      },
      text: {
        primary: '#f8fafc',
        secondary: '#cbd5e1',
        accent: '#60a5fa',
      },
      border: {
        primary: '#334155',
        secondary: '#475569',
      },
      gradient: {
        primary: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        secondary: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
      },
    },
    styles: {
      sidebar: {
        background: 'bg-slate-800',
        border: 'border-slate-700',
      },
      header: {
        background: 'bg-slate-800',
        border: 'border-slate-700',
      },
      card: {
        background: 'bg-slate-800',
        border: 'border-slate-700',
        shadow: 'shadow-lg hover:shadow-xl',
      },
    },
  },
  royal: {
    id: 'royal',
    name: 'Royal Purple',
    colors: {
      primary: {
        50: '#faf5ff',
        100: '#f3e8ff',
        200: '#e9d5ff',
        300: '#d8b4fe',
        400: '#c084fc',
        500: '#a855f7',
        600: '#9333ea',
        700: '#7c3aed',
        800: '#6b21a8',
        900: '#581c87',
      },
      background: {
        primary: '#fdfcff',
        secondary: '#ffffff',
        card: '#ffffff',
        hover: '#faf5ff',
      },
      text: {
        primary: '#1f1729',
        secondary: '#6b7280',
        accent: '#9333ea',
      },
      border: {
        primary: '#f3f4f6',
        secondary: '#e5e7eb',
      },
      gradient: {
        primary: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        secondary: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      },
    },
    styles: {
      sidebar: {
        background: 'bg-white',
        border: 'border-gray-200',
      },
      header: {
        background: 'bg-white',
        border: 'border-gray-200',
      },
      card: {
        background: 'bg-white',
        border: 'border-gray-200',
        shadow: 'shadow-sm hover:shadow-md',
      },
    },
  },
};

interface ThemeContextType {
  currentTheme: Theme;
  setTheme: (themeId: string) => void;
  themes: Record<string, Theme>;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [currentThemeId, setCurrentThemeId] = useState<string>(() => {
    return localStorage.getItem('selectedTheme') || 'ocean';
  });

  const currentTheme = themes[currentThemeId];

  const setTheme = (themeId: string) => {
    if (themes[themeId]) {
      setCurrentThemeId(themeId);
      localStorage.setItem('selectedTheme', themeId);
    }
  };

  useEffect(() => {
    // Apply theme CSS variables to document root
    const root = document.documentElement;
    const theme = currentTheme;
    
    // Set CSS custom properties
    Object.entries(theme.colors.primary).forEach(([key, value]) => {
      root.style.setProperty(`--color-primary-${key}`, value);
    });

    root.style.setProperty('--color-background-primary', theme.colors.background.primary);
    root.style.setProperty('--color-background-secondary', theme.colors.background.secondary);
    root.style.setProperty('--color-background-card', theme.colors.background.card);
    root.style.setProperty('--color-background-hover', theme.colors.background.hover);
    
    root.style.setProperty('--color-text-primary', theme.colors.text.primary);
    root.style.setProperty('--color-text-secondary', theme.colors.text.secondary);
    root.style.setProperty('--color-text-accent', theme.colors.text.accent);
    
    root.style.setProperty('--color-border-primary', theme.colors.border.primary);
    root.style.setProperty('--color-border-secondary', theme.colors.border.secondary);
    
    root.style.setProperty('--gradient-primary', theme.colors.gradient.primary);
    root.style.setProperty('--gradient-secondary', theme.colors.gradient.secondary);

    // Update document background
    document.body.style.backgroundColor = theme.colors.background.primary;
  }, [currentTheme]);

  const value = {
    currentTheme,
    setTheme,
    themes,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};